import { partition } from 'lodash';
import { useCallback, useState } from 'react';

import {
  ManageIntegrationDataPane,
  PaneForm,
} from '../ManageIntegrationDataModal.types';

export type PaneForms = Partial<Record<ManageIntegrationDataPane, PaneForm>>;

export interface UseStepCommitArgs {
  paneForms: PaneForms;
  onPaneBlocked: (pane: ManageIntegrationDataPane) => void;
  onPaneFailure: (pane: ManageIntegrationDataPane, error: unknown) => void;
}

export interface StepCommit {
  isSaving: boolean;
  commitPanes: (panes: ManageIntegrationDataPane[]) => Promise<boolean>;
  requestClose: (close: () => void) => void;
}

/**
 * Puts the Import data → Entities pane after the other panes and keeps the
 * order of the other panes.
 *
 * When the Entities pane imports an account, the backend deletes the default
 * `PRE_CREATED` account of the same kind and owners. The Liabilities and Life
 * insurance panes can select a default account. When they save, they set it
 * to `ACTIVE`, and the backend does not delete active accounts. If the
 * Entities pane saves first, the backend can delete the selected account, and
 * the other pane fails with `NOT_FOUND`.
 */
function orderPanesForCommit(
  panes: ManageIntegrationDataPane[]
): ManageIntegrationDataPane[] {
  const [entityPanes, otherPanes] = partition(
    panes,
    (pane) => pane === ManageIntegrationDataPane.ImportDataEntities
  );
  return [...otherPanes, ...entityPanes];
}

export function useStepCommit({
  paneForms,
  onPaneBlocked,
  onPaneFailure,
}: UseStepCommitArgs): StepCommit {
  const [isSaving, setIsSaving] = useState(false);

  const commitPanes = useCallback(
    async (panes: ManageIntegrationDataPane[]) => {
      const built = orderPanesForCommit(
        panes.filter((pane) => paneForms[pane])
      );
      if (!built.length) return true;

      setIsSaving(true);
      try {
        for (const pane of built) {
          try {
            const result = await paneForms[pane]?.save();
            if (result === false) {
              onPaneBlocked(pane);
              return false;
            }
          } catch (error) {
            onPaneFailure(pane, error);
            return false;
          }
        }
      } finally {
        setIsSaving(false);
      }
      return true;
    },
    [onPaneBlocked, onPaneFailure, paneForms]
  );

  const requestClose = useCallback(
    (close: () => void) => {
      if (isSaving) return;
      close();
    },
    [isSaving]
  );

  return { isSaving, commitPanes, requestClose };
}
